import React, { useState, useEffect, useRef } from 'react';
import { Search, Upload, Image, FileText, Folder, Eye } from 'lucide-react';
import api from '../services/api';

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const DatasetSelector = ({ onSelect }) => {
  const [datasets, setDatasets] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [kaggleDatasets, setKaggleDatasets] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('uploaded');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/datasets', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      setDatasets(prev => [...prev, response.data]);
      onSelect(response.data);
    } catch (error) {
      console.error('Error uploading dataset:', error);
      alert('Error uploading file: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

 const handleFolderUpload = async (files) => {
  if (!files || files.length === 0) return;
  
  setIsUploading(true);
  setUploadProgress(0);
  
  try {
    const formData = new FormData();
    
    // Add dataset name
    const folderName = files[0].webkitRelativePath ? 
      files[0].webkitRelativePath.split('/')[0] : 
      'image_dataset';
    
    formData.append('datasetName', folderName);
    formData.append('datasetType', 'image_classification');
    
    // Debug: Log file structure
    console.log('=== FRONTEND FILE DEBUG ===');
    console.log(`Total files: ${files.length}`);
    console.log('First 10 files:');
    Array.from(files).slice(0, 10).forEach((file, index) => {
      console.log(`File ${index}:`, {
        name: file.name,
        webkitRelativePath: file.webkitRelativePath,
        size: file.size,
        type: file.type
      });
    });
    
    // Count files by folder to verify structure
    const folderCounts = {};
    Array.from(files).forEach((file) => {
      if (file.webkitRelativePath) {
        const parts = file.webkitRelativePath.split('/');
        if (parts.length >= 2) {
          const folder = parts[parts.length - 2]; // Parent folder
          folderCounts[folder] = (folderCounts[folder] || 0) + 1;
        }
      }
    });
    
    console.log('Files per folder:', folderCounts);
    console.log('Number of folders:', Object.keys(folderCounts).length);
    console.log('=============================');
    
    // Append files with preserved paths
    Array.from(files).forEach((file, index) => {
      // Use webkitRelativePath if available, otherwise use name
      const relativePath = file.webkitRelativePath || file.name;
      
      // Append with the relative path preserved
      formData.append('files', file, relativePath);
    });
    
    console.log('Uploading folder:', folderName, 'with', files.length, 'files');
    
    const response = await api.post('/datasets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      }
    });
    
    console.log('Upload response:', response.data);
    
    if (!response.data.metadata) {
      console.error('Response missing metadata:', response.data);
      throw new Error('Server response missing metadata');
    }
    
    // Verify the number of classes makes sense
    const numClasses = response.data.metadata.classes?.length || 0;
    if (numClasses > 50) {
      console.warn(`Warning: Found ${numClasses} classes, which seems unusually high for image classification`);
    }
    
    setDatasets(prev => [...prev, response.data]);
    onSelect(response.data);
    
  } catch (error) {
    console.error('Error uploading image dataset:', error);
    console.error('Error details:', error.response?.data);
    alert('Error uploading images: ' + (error.response?.data?.message || error.message));
  } finally {
    setIsUploading(false);
    setUploadProgress(0);
  }
};

  useEffect(() => {
    fetchDatasets();
  }, []);

  const fetchDatasets = async () => {
    try {
      const response = await api.get('/datasets');
      setDatasets(response.data);
    } catch (error) {
      console.error('Error fetching datasets:', error);
    }
  };

  const searchKaggleDatasets = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await api.get('/datasets/kaggle/search', {
        params: { keyword: searchQuery }
      });
      setKaggleDatasets(response.data);
    } catch (error) {
      console.error('Error searching Kaggle datasets:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const downloadKaggleDataset = async (dataset) => {
    setIsUploading(true);
    try {
      if (!dataset.owner || !dataset.datasetSlug) {
        throw new Error('Invalid dataset reference');
      }
      
      const datasetRef = `${dataset.owner}/${dataset.datasetSlug}`;
      const response = await api.post(`/datasets/kaggle/download/${encodeURIComponent(datasetRef)}`);
      
      setDatasets((prev) => [...prev, response.data]);
      onSelect(response.data);
      setActiveTab('uploaded');
    } catch (error) {
      console.error('Error downloading Kaggle dataset:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const DatasetCard = ({ dataset }) => {
  // Add more robust checking
  const isImage = dataset.metadata?.type === 'image_classification';
  
  // Better error handling for missing metadata
  if (!dataset.metadata) {
    console.warn('Dataset missing metadata:', dataset);
    return (
      <div className="flex items-center justify-between p-3 border border-red-200 rounded-lg bg-red-50">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-red-100">
            <FileText size={20} className="text-red-600" />
          </div>
          <div>
            <p className="font-medium">{dataset.name}</p>
            <p className="text-sm text-red-500">Processing failed - metadata missing</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Helper function to get the appropriate description
  const getDescription = () => {
    if (isImage) {
      const totalImages = dataset.metadata?.totalImages || 0;
      const classCount = dataset.metadata?.classes?.length || 0;
      return `${totalImages} images • ${classCount} classes • ${formatBytes(dataset.size || 0)}`;
    } else {
      const rowCount = dataset.metadata?.rowCount || 0;
      return `${rowCount} rows • ${formatBytes(dataset.size || 0)}`;
    }
  };
  
  return (
    <div
      onClick={() => onSelect(dataset)}
      className="flex items-center justify-between p-3 border border-gray-100 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 shadow-sm"
    >
      <div className="flex items-center space-x-3">
        <div className={`p-2 rounded-lg ${isImage ? 'bg-purple-100' : 'bg-blue-100'}`}>
          {isImage ? (
            <Image size={20} className="text-purple-600" />
          ) : (
            <FileText size={20} className="text-blue-600" />
          )}
        </div>
        <div>
          <p className="font-medium">{dataset.name}</p>
          <p className="text-sm text-gray-500">
            {getDescription()}
          </p>
        </div>
      </div>
      
      {isImage && dataset.metadata?.previewImages && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedDataset(dataset);
            setShowPreviewModal(true);
          }}
          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
        >
          <Eye size={16} />
        </button>
      )}
    </div>
  );
};

  const PreviewModal = ({ dataset, onClose }) => {
    if (!dataset) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold">{dataset.name}</h3>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                ×
              </button>
            </div>
            <p className="text-gray-600 mt-2">
              {dataset.metadata.totalImages} images across {dataset.metadata.classes.length} classes
            </p>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {Object.entries(dataset.metadata.previewImages || {}).map(([className, images]) => (
              <div key={className} className="mb-6">
                <h4 className="font-semibold mb-3 flex items-center">
                  <Folder size={16} className="mr-2 text-blue-600" />
                  {className}
                  <span className="ml-2 text-sm text-gray-500">
                    ({dataset.metadata.classStats.find(c => c.name === className)?.count || 0} images)
                  </span>
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {images.map((img, idx) => (
                    <div key={idx} className="text-center">
                      <img
                        src={`data:image/jpeg;base64,${img.thumbnail}`}
                        alt={img.name}
                        className="w-full h-32 object-cover rounded-lg border"
                      />
                      <p className="text-xs text-gray-500 mt-1 truncate">{img.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-4">
      {/* Tab navigation */}
      <div className="flex border-b mb-4">
        <button
          className={`px-4 py-2 ${activeTab === 'uploaded' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
          onClick={() => setActiveTab('uploaded')}
        >
          My Datasets
        </button>
        <button
          className={`px-4 py-2 ${activeTab === 'kaggle' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
          onClick={() => setActiveTab('kaggle')}
        >
          Kaggle Datasets
        </button>
      </div>

      {activeTab === 'uploaded' ? (
        <div className="space-y-4">
          {/* Upload Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* CSV Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => handleFileUpload(e.target.files[0])}
                className="hidden"
                accept=".csv,.xlsx,.xls"
              />
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <FileText size={40} className="text-blue-500 mb-3" />
                <span className="text-gray-700 font-medium">Upload CSV Dataset</span>
                <p className="text-sm text-gray-500 mt-1 text-center">
                  For traditional ML tasks<br/>
                  Max size: 10MB
                </p>
              </div>
            </div>

            {/* Image Upload */}
            <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 hover:border-purple-400 transition-colors">
              <input
                ref={folderInputRef}
                type="file"
                onChange={(e) => handleFolderUpload(e.target.files)}
                className="hidden"
                webkitdirectory=""
                multiple
                accept="image/*"
              />
              <div 
                onClick={() => folderInputRef.current?.click()}
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Image size={40} className="text-purple-500 mb-3" />
                <span className="text-gray-700 font-medium">Upload Image Dataset</span>
                <p className="text-sm text-gray-500 mt-1 text-center">
                  For image classification<br/>
                  Organized in folders by class
                </p>
              </div>
            </div>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Upload size={20} className="text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">Uploading dataset...</p>
                  <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">{uploadProgress}% complete</p>
                </div>
              </div>
            </div>
          )}

          {/* Datasets List */}
          {datasets.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Your Datasets ({datasets.length})
              </h3>
              <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white text-black shadow-inner p-2">
                <div className="space-y-2 px-1">
                  {datasets.map((dataset) => (
                    <DatasetCard key={dataset._id} dataset={dataset} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Kaggle search UI - keeping existing implementation */
        <div className="space-y-4">
          <div className="flex space-x-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchKaggleDatasets()}
              placeholder="Search Kaggle datasets..."
              className="flex-1 px-4 py-2 border text-black rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={searchKaggleDatasets}
              disabled={isSearching}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {isSearching ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white " />
              ) : (
                <Search className="w-5 h-5" />
              )}
            </button>
          </div>

          {kaggleDatasets.length > 0 && (
            <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-inner p-2 text-black">
              <div className="space-y-2 px-1">
                {kaggleDatasets.map((dataset) => (
                  <div
                    key={dataset.id}
                    className="p-4 border border-gray-100 rounded-lg hover:bg-green-50 hover:border-green-200 transition-all duration-200 shadow-sm text-black"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium">{dataset.title}</h3>
                        <p className="text-sm text-gray-500 mt-1">{dataset.description}</p>
                        <div className="flex space-x-4 mt-2 text-sm text-gray-500">
                          <span>{formatBytes(dataset.size)}</span>
                          <span>•</span>
                          <span>{dataset.downloadCount} downloads</span>
                        </div>
                      </div>
                      <button
                        onClick={() => downloadKaggleDataset(dataset)}
                        disabled={isUploading}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200 shadow-sm"
                      >
                        {isUploading ? 'Downloading...' : 'Use Dataset'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Preview Modal */}
      {showPreviewModal && (
        <PreviewModal 
          dataset={selectedDataset} 
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedDataset(null);
          }} 
        />
      )}
    </div>
  );
};

export default DatasetSelector;